import LegalShell from "@/components/LegalShell";

export default function Privacy() {
    return (
        <LegalShell
            title="Privacy Policy"
            subtitle="What we collect, why we collect it, and how we keep it safe."
            lastUpdated="February 2026"
        >
            <h2>1. The short version</h2>
            <p>
                Bosun's Mate is a vessel management app. We collect the data you give
                us when you create an account or log entries about your vessel. We do
                not sell your data, ever. We do not embed third-party advertising
                trackers. We use Stripe for payments and IONOS for transactional
                email — that's it.
            </p>

            <h2>2. What we collect</h2>
            <ul>
                <li><strong>Account data:</strong> name, email, hashed password (we never see your plaintext password).</li>
                <li><strong>Vessel data you enter:</strong> vessel name, type, photos, engine hours, entries (maintenance, fuel, receipts, parts, etc.).</li>
                <li><strong>Subscription data:</strong> plan, status, Stripe customer ID. Payment card details are handled directly by Stripe and never touch our servers.</li>
                <li><strong>Authentication metadata:</strong> session tokens (HTTP-only cookies), login timestamps.</li>
            </ul>

            <h2>3. What we do NOT collect</h2>
            <ul>
                <li>We do not track your location in the background — see the next section for how we handle GPS.</li>
                <li>We do not collect device identifiers, IDFA, advertising IDs.</li>
                <li>We do not embed third-party analytics or session-recording scripts.</li>
                <li>We do not access your phone's contacts, camera roll, or microphone unless you explicitly attach a photo to an entry.</li>
            </ul>

            <h2>4. Location data (foreground only, opt-in per entry)</h2>
            <p>
                When you tap <strong>"Add my current location"</strong> on a Cruising Log entry, Bosun's Mate
                asks your device for one GPS fix at that moment. We use it to:
            </p>
            <ul>
                <li>Save the latitude/longitude with that single log entry, so you can find it later.</li>
                <li>Resolve the coordinates to a human-readable place name (e.g. "Princess Louisa Inlet, BC")
                    via OpenStreetMap's free Nominatim service. We send only the coordinates — no account
                    info — and they return a place name.</li>
                <li>Optionally auto-fill the "where met" field on any Cruiser Contacts you linked to that
                    entry, so your address book remembers where you crossed paths (only if those fields are
                    currently empty — we never overwrite something you've typed).</li>
            </ul>
            <p>
                We <strong>never</strong> poll your location in the background. We <strong>never</strong> track
                you between log entries. The only time we read your GPS is in the second you tap the button.
                You can revoke the permission any time in your device settings — the app keeps working with
                location features simply disabled.
            </p>

            <h2>5. How we use it</h2>
            <ul>
                <li>To show you your vessel data and let you edit it.</li>
                <li>To send you transactional emails (welcome, password reset, billing receipts, trial-ending reminders).</li>
                <li>To process subscription payments via Stripe.</li>
            </ul>

            <h2>6. Who we share it with</h2>
            <ul>
                <li><strong>Stripe (payment processor):</strong> handles all billing. Their privacy policy: <a className="bm-link" href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a>.</li>
                <li><strong>IONOS (email delivery):</strong> delivers transactional emails on our behalf.</li>
                <li><strong>Emergent-managed Google Auth (optional):</strong> only if you choose "Sign in with Google" — we receive your email, name, and profile picture from Google.</li>
            </ul>
            <p>We do <em>not</em> share, sell, rent, or trade your data with anyone else.</p>

            <h2>7. Where your data lives</h2>
            <p>
                Encrypted at rest in our managed MongoDB database. Transferred over
                HTTPS/TLS only. Backed up daily.
            </p>

            <h2>8. Your rights</h2>
            <ul>
                <li><strong>Access:</strong> log in to see all your data.</li>
                <li><strong>Export:</strong> use the "Export to PDF" feature for any vessel.</li>
                <li><strong>Deletion:</strong> email <a className="bm-link" href="mailto:captain@bosunsmate.app">captain@bosunsmate.app</a> with "Delete my account" and we'll remove everything within 14 days.</li>
                <li><strong>Correction:</strong> edit anything inside the app, anytime.</li>
            </ul>

            <h2>9. Children</h2>
            <p>Bosun's Mate is not directed at children under 13. We do not knowingly collect data from anyone under 13.</p>

            <h2>10. Changes</h2>
            <p>
                If we materially change this policy, we'll email you at least 30 days
                before the changes take effect.
            </p>

            <h2>11. Contact</h2>
            <p>
                Questions? Email <a className="bm-link" href="mailto:captain@bosunsmate.app">captain@bosunsmate.app</a>.
            </p>
        </LegalShell>
    );
}
